// frontend/src/pages/emailTemplates.js
// ✉️ Alapító-megkeresés sablonjai a user app-nyelvén.
//
// A nyelvlista az app assets/translations mappájából jön (15 nyelv). A címzett
// users/{uid}.appLanguage mezője dönti el, melyiket kapja; ismeretlen vagy
// hiányzó nyelvnél az ANGOL a tartalék — ezért az 'en' kötelező sablon.
//
// {{name}}     → a címzett KERESZTNEVE (az első szó a névből)
// {{fullname}} → a TELJES név
//
// A magyar és a kelet-ázsiai (zh/ja/ko) sablonok szándékosan {{fullname}}-et
// használnak: ezekben a nyelvekben a vezetéknév áll elöl, így a "keresztnév"
// kivonása a vezetéknevet adná ("Kedves Barta!" a "Barta Boglárka" névből).
// A teljes név mindkét sorrend mellett helyes marad.
// Név hiányában nyelvfüggő megszólítás lép be — lásd FALLBACK_NAME.

export const LANGS = [
  { code: 'hu', label: 'Magyar', flag: '🇭🇺' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
];

export const FALLBACK_LANG = 'en';
export const LANG_CODES = LANGS.map((l) => l.code);
export const langLabel = (c) => {
  const l = LANGS.find((x) => x.code === c);
  return l ? `${l.flag} ${l.label}` : c;
};

// Közös aláírás — a névnél és a cégnévnél nem fordítunk
const SIG = 'Bódi Gábor\nFounder & CEO · AI TourMate Kft.\ninfo@aitourmate.eu · www.aitourmate.eu';

export const TEMPLATES = {
  hu: {
    subject: 'Egy gyors kérdés az AI TourMate alapítójától',
    bodyText: `Kedves {{fullname}}!

Gábor vagyok, az AI TourMate alapítója. Láttam, hogy használtad az appot, és személyesen szerettem volna megkérdezni: őszintén mi a véleményed róla?

Egyszemélyes fejlesztőként nagyon sokat számít a valódi visszajelzés azoktól, akik tényleg használják — ez alapján döntöm el, mit fejlesszek tovább. Pár dolog, amire kíváncsi vagyok:

- Mi tetszett benne, ha tetszett valami?
- Mi volt zavaró, érthetetlen vagy hiányos?
- Van olyan, amit szeretnél, hogy tudjon?

Semmi formaság nem kell — elég, ha válaszolsz erre az e-mailre pár mondatban. A legőszintébb vélemény a leghasznosabb, legyen az jó vagy rossz.

Köszönöm, hogy kipróbáltad, és hogy szánsz egy percet a véleményedre.

Üdv,
${SIG}`,
  },

  en: {
    subject: 'A quick question from the founder of AI TourMate',
    bodyText: `Hi {{name}},

I'm Gábor, the founder of AI TourMate. I saw that you've been using the app, and I wanted to ask you personally: what do you honestly think of it?

As a solo developer, real feedback from people who actually use it means a great deal — it's what I base my decisions on about what to build next. A few things I'm curious about:

- What did you like, if anything?
- What was confusing, unclear or missing?
- Is there something you wish it could do?

No formality needed — just reply to this email in a couple of sentences. The most honest opinion is the most useful one, good or bad.

Thank you for giving it a try, and for taking a minute to share your thoughts.

Best regards,
${SIG}`,
  },

  de: {
    subject: 'Eine kurze Frage vom Gründer von AI TourMate',
    bodyText: `Hallo {{name}},

ich bin Gábor, der Gründer von AI TourMate. Ich habe gesehen, dass du die App genutzt hast, und wollte dich persönlich fragen: Was hältst du ehrlich gesagt davon?

Als Einzelentwickler ist mir echtes Feedback von Menschen, die die App wirklich benutzen, sehr wichtig — danach entscheide ich, was ich als Nächstes entwickle. Ein paar Dinge, die mich interessieren:

- Was hat dir gefallen, falls überhaupt etwas?
- Was war verwirrend, unverständlich oder hat gefehlt?
- Gibt es etwas, das die App können sollte?

Ganz ohne Förmlichkeiten — antworte einfach mit ein paar Sätzen auf diese E-Mail. Die ehrlichste Meinung ist die nützlichste, ob gut oder schlecht.

Danke, dass du sie ausprobiert hast und dir eine Minute für dein Feedback nimmst.

Viele Grüße,
${SIG}`,
  },

  fr: {
    subject: "Une petite question du fondateur d'AI TourMate",
    bodyText: `Bonjour {{name}},

Je suis Gábor, le fondateur d'AI TourMate. J'ai vu que vous avez utilisé l'application, et je voulais vous demander personnellement : qu'en pensez-vous sincèrement ?

En tant que développeur indépendant, les retours de ceux qui l'utilisent vraiment comptent énormément — c'est sur cette base que je décide quoi développer ensuite. Quelques points qui m'intéressent :

- Qu'est-ce qui vous a plu, s'il y a quelque chose ?
- Qu'est-ce qui était déroutant, peu clair ou manquant ?
- Y a-t-il une fonctionnalité que vous aimeriez y voir ?

Aucune formalité nécessaire — répondez simplement à cet e-mail en quelques phrases. L'avis le plus sincère est le plus utile, qu'il soit positif ou négatif.

Merci de l'avoir essayée et de prendre une minute pour partager votre avis.

Cordialement,
${SIG}`,
  },

  it: {
    subject: 'Una domanda veloce dal fondatore di AI TourMate',
    bodyText: `Ciao {{name}},

sono Gábor, il fondatore di AI TourMate. Ho visto che hai usato l'app e volevo chiedertelo personalmente: cosa ne pensi sinceramente?

Da sviluppatore solitario, un riscontro autentico da chi la usa davvero conta moltissimo — è su questo che decido cosa sviluppare in seguito. Alcune cose che mi incuriosiscono:

- Cosa ti è piaciuto, se qualcosa ti è piaciuto?
- Cosa è stato confuso, poco chiaro o mancante?
- C'è qualcosa che vorresti riuscisse a fare?

Nessuna formalità — basta rispondere a questa e-mail con due righe. L'opinione più sincera è la più utile, che sia positiva o negativa.

Grazie per averla provata e per aver dedicato un minuto al tuo parere.

Un saluto,
${SIG}`,
  },

  es: {
    subject: 'Una pregunta rápida del fundador de AI TourMate',
    bodyText: `Hola {{name}}:

Soy Gábor, el fundador de AI TourMate. He visto que has usado la aplicación y quería preguntarte personalmente: ¿qué opinas sinceramente de ella?

Como desarrollador en solitario, la opinión real de quienes la usan de verdad significa muchísimo — es en lo que me baso para decidir qué desarrollar a continuación. Algunas cosas que me interesan:

- ¿Qué te ha gustado, si es que algo te ha gustado?
- ¿Qué te ha resultado confuso, poco claro o insuficiente?
- ¿Hay algo que te gustaría que pudiera hacer?

Sin formalidades — basta con que respondas a este correo en un par de frases. La opinión más sincera es la más útil, sea buena o mala.

Gracias por probarla y por dedicar un minuto a compartir tu opinión.

Un saludo,
${SIG}`,
  },

  pl: {
    subject: 'Krótkie pytanie od twórcy AI TourMate',
    bodyText: `Cześć {{name}},

nazywam się Gábor i jestem twórcą AI TourMate. Zauważyłem, że korzystałeś(-aś) z aplikacji, i chciałem zapytać osobiście: co szczerze o niej sądzisz?

Jako jednoosobowy zespół bardzo cenię prawdziwe opinie osób, które faktycznie z niej korzystają — to na ich podstawie decyduję, co rozwijać dalej. Kilka rzeczy, które mnie ciekawią:

- Co Ci się spodobało, jeśli cokolwiek?
- Co było mylące, niejasne albo czego zabrakło?
- Czy jest coś, co chciałbyś(-abyś), żeby potrafiła?

Bez żadnych formalności — wystarczy, że odpiszesz na tego maila w kilku zdaniach. Najszczersza opinia jest najbardziej przydatna, dobra czy zła.

Dziękuję za wypróbowanie aplikacji i za poświęcenie chwili na opinię.

Pozdrawiam,
${SIG}`,
  },

  pt: {
    subject: 'Uma pergunta rápida do fundador da AI TourMate',
    bodyText: `Olá {{name}},

sou o Gábor, fundador da AI TourMate. Vi que usou a aplicação e queria perguntar-lhe pessoalmente: o que acha sinceramente dela?

Como programador a trabalhar sozinho, a opinião real de quem a usa de facto conta imenso — é com base nisso que decido o que desenvolver a seguir. Algumas coisas que me interessam:

- O que gostou, se é que gostou de alguma coisa?
- O que foi confuso, pouco claro ou ficou a faltar?
- Há algo que gostaria que a aplicação fizesse?

Sem formalidades — basta responder a este e-mail com duas ou três frases. A opinião mais sincera é a mais útil, seja boa ou má.

Obrigado por a ter experimentado e por dedicar um minuto à sua opinião.

Com os melhores cumprimentos,
${SIG}`,
  },

  ro: {
    subject: 'O întrebare scurtă de la fondatorul AI TourMate',
    bodyText: `Bună {{name}},

sunt Gábor, fondatorul AI TourMate. Am văzut că ai folosit aplicația și am vrut să te întreb personal: ce părere ai sincer despre ea?

Ca dezvoltator care lucrează singur, feedbackul real de la cei care o folosesc cu adevărat contează enorm — pe baza lui decid ce să dezvolt mai departe. Câteva lucruri care mă interesează:

- Ce ți-a plăcut, dacă ți-a plăcut ceva?
- Ce a fost derutant, neclar sau a lipsit?
- Există ceva ce ai vrea să poată face?

Fără formalități — e suficient să răspunzi la acest e-mail în câteva propoziții. Cea mai sinceră părere este cea mai utilă, fie ea bună sau rea.

Îți mulțumesc că ai încercat-o și că îți faci un minut pentru părerea ta.

Cu stimă,
${SIG}`,
  },

  ru: {
    subject: 'Короткий вопрос от основателя AI TourMate',
    bodyText: `Здравствуйте, {{name}}!

Меня зовут Габор, я основатель AI TourMate. Я увидел, что вы пользовались приложением, и хотел спросить лично: что вы о нём честно думаете?

Я разрабатываю его один, поэтому настоящий отзыв от тех, кто им действительно пользуется, значит очень много — именно на этом я основываю решения о том, что делать дальше. Меня интересует несколько вещей:

- Что вам понравилось, если понравилось что-то?
- Что было непонятным, запутанным или чего не хватило?
- Есть ли что-то, что вы хотели бы в нём видеть?

Никаких формальностей — просто ответьте на это письмо парой предложений. Самый честный отзыв самый полезный, хороший он или плохой.

Спасибо, что попробовали приложение и уделили минуту своему мнению.

С уважением,
${SIG}`,
  },

  sk: {
    subject: 'Krátka otázka od zakladateľa AI TourMate',
    bodyText: `Ahoj {{name}},

som Gábor, zakladateľ AI TourMate. Všimol som si, že si aplikáciu používal(a), a chcel som sa osobne spýtať: čo si o nej úprimne myslíš?

Ako jednotlivec, ktorý ju vyvíja sám, si veľmi cením skutočnú spätnú väzbu od ľudí, ktorí ju naozaj používajú — podľa toho sa rozhodujem, čo vyvíjať ďalej. Zaujíma ma najmä:

- Čo sa ti páčilo, ak sa ti niečo páčilo?
- Čo bolo mätúce, nezrozumiteľné alebo čo chýbalo?
- Je niečo, čo by si chcel(a), aby aplikácia vedela?

Žiadne formality — stačí, ak na tento e-mail odpovieš pár vetami. Najúprimnejší názor je ten najužitočnejší, či už dobrý alebo zlý.

Ďakujem, že si ju vyskúšal(a) a že si nájdeš chvíľu na svoj názor.

S pozdravom,
${SIG}`,
  },

  sl: {
    subject: 'Kratko vprašanje od ustanovitelja AI TourMate',
    bodyText: `Pozdravljeni, {{name}},

sem Gábor, ustanovitelj AI TourMate. Opazil sem, da ste aplikacijo uporabljali, in vas želim osebno vprašati: kaj si iskreno mislite o njej?

Ker jo razvijam sam, mi je pristna povratna informacija tistih, ki jo resnično uporabljajo, zelo dragocena — na tej podlagi se odločam, kaj razvijati naprej. Zanima me predvsem:

- Kaj vam je bilo všeč, če vam je kaj bilo?
- Kaj je bilo zmedeno, nejasno ali je manjkalo?
- Je kaj, kar bi si želeli, da zna?

Brez formalnosti — dovolj je, da na to sporočilo odgovorite z nekaj stavki. Najbolj iskreno mnenje je najbolj uporabno, naj bo dobro ali slabo.

Hvala, da ste jo preizkusili in si vzeli minuto za svoje mnenje.

Lep pozdrav,
${SIG}`,
  },

  zh: {
    subject: '来自 AI TourMate 创始人的一个小问题',
    bodyText: `{{fullname}} 您好：

我是 Gábor，AI TourMate 的创始人。我注意到您使用过这款应用，想亲自问一句：您对它的真实看法是什么？

我是独立开发者，真正使用者的坦率反馈对我非常重要——我正是依据这些来决定接下来开发什么。我特别想了解：

- 有让您满意的地方吗？是哪些？
- 哪里让您感到困惑、难以理解，或者有所欠缺？
- 有没有您希望它能做到的功能？

不必拘礼——直接回复这封邮件，写上几句话就够了。无论好评还是差评，最坦诚的意见就是最有用的。

感谢您试用，也感谢您抽出一分钟分享看法。

顺祝安好，
${SIG}`,
  },

  ja: {
    subject: 'AI TourMate 創業者からの短いご質問',
    bodyText: `{{fullname}} 様

AI TourMate を作っている Gábor と申します。アプリをお使いいただいたのを拝見し、直接お伺いしたく連絡いたしました。率直なところ、いかがでしたでしょうか。

個人で開発しているため、実際に使ってくださった方の本音のご意見はとても貴重です。次に何を作るかは、そうしたお声をもとに決めています。特にお伺いしたいのは次の点です。

- よかった点があれば、どこでしょうか
- わかりにくかった点、混乱した点、足りないと感じた点はありますか
- こんなことができればという機能はありますか

かしこまらなくて結構です。このメールに数行でご返信いただければ十分です。良い評価でも厳しい評価でも、率直なご意見が一番役に立ちます。

お試しいただき、またお時間を割いてくださり、ありがとうございます。

よろしくお願いいたします。
${SIG}`,
  },

  ko: {
    subject: 'AI TourMate 창업자가 드리는 짧은 질문',
    bodyText: `{{fullname}}님 안녕하세요,

AI TourMate를 만든 Gábor입니다. 앱을 사용해 주신 것을 보고 직접 여쭙고 싶어 연락드립니다. 솔직히 어떠셨는지 궁금합니다.

혼자 개발하고 있어서, 실제로 사용해 주신 분들의 솔직한 의견이 정말 큰 도움이 됩니다. 다음에 무엇을 만들지도 그 의견을 바탕으로 정하고 있습니다. 특히 이런 점이 궁금합니다:

- 마음에 드신 부분이 있다면 무엇인가요?
- 혼란스럽거나 이해하기 어렵거나 부족했던 점은 무엇인가요?
- 앱이 이런 것도 해주면 좋겠다 싶은 기능이 있으신가요?

격식은 필요 없습니다. 이 메일에 몇 줄만 답장해 주시면 충분합니다. 좋은 의견이든 아쉬운 의견이든, 솔직한 말씀이 가장 도움이 됩니다.

사용해 주셔서, 그리고 잠시 시간을 내어 의견을 들려주셔서 감사합니다.

감사합니다.
${SIG}`,
  },
};

// Név nélküli címzett megszólítása nyelvenként (a {{name}} helyére)
export const FALLBACK_NAME = {
  hu: 'Utazó', en: 'there', de: 'zusammen', fr: 'à vous', it: 'a te', es: 'viajero',
  pl: 'podróżniku', pt: 'viajante', ro: 'călătorule', ru: 'путешественник',
  sk: 'cestovateľ', sl: 'popotnik', zh: '旅行者', ja: 'ご利用者', ko: '여행자',
};
